import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBookRequest } from "./hooks/useBookRequest";
import { showToast } from "../../../core/utils/alert";
import { all_routes } from "../../router/all_routes";
import { useReviewBookRequestMutation } from "./api/bookRequestApi";

interface BookRequestModalsProps {
  requestId?: number;
  onSuccess?: () => void;
  modalType?: "delete" | "approve" | "reject" | "complete";
  rejectReason?: string;
  setRejectReason?: (reason: string) => void;
}

const BookRequestModals: React.FC<BookRequestModalsProps> = ({
  requestId,
  onSuccess,
  rejectReason = "",
  setRejectReason,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { deleteBookRequest } = useBookRequest();
  const [reviewBookRequest] = useReviewBookRequestMutation();
  const navigate = useNavigate();
  const routes = all_routes;

  // Shared styles for modal icons based on _popup.scss
  const iconStyle = {
    width: "60px",
    height: "60px",
    borderRadius: "5px",
    margin: "0 auto 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "40px",
  };

  // Specific color styles for different icon types using variables from _variables.scss
  const deleteIconStyle = {
    ...iconStyle,
    backgroundColor: "#FDE9ED", // $danger-100
    color: "#E82646", // $danger
  };

  const approveIconStyle = {
    ...iconStyle,
    backgroundColor: "#E8F9E8", // $success-100
    color: "#1ABE17", // $success
  };

  const completeIconStyle = {
    ...iconStyle,
    backgroundColor: "#E7F1FC", // $info-100
    color: "#0F65CD", // $info
  };

  const rejectIconStyle = {
    ...iconStyle,
    backgroundColor: "#FDE9ED", // $danger-100
    color: "#E82646", // $danger
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!requestId) return;

    setIsDeleting(true);
    try {
      const result = await deleteBookRequest(requestId);

      if (result.success) {
        showToast({
          title: "Success",
          text: "Book request deleted successfully",
          icon: "success",
        });

        if (onSuccess) {
          onSuccess();
        } else {
          navigate(routes.bookRequestList);
        }
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not delete the book request",
          icon: "error",
        });
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Could not delete the book request",
        icon: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReview = async (
    e: React.FormEvent,
    status: "approved" | "rejected" | "completed"
  ) => {
    e.preventDefault();

    if (!requestId) return;

    setIsProcessing(true);
    try {
      const result = await reviewBookRequest({
        id: requestId,
        status,
        reviewer_note: status === "rejected" ? rejectReason : undefined,
      }).unwrap();

      if (result.success) {
        showToast({
          title: "Success",
          text: `Book request ${status} successfully`,
          icon: "success",
        });

        if (onSuccess) {
          onSuccess();
        } else {
          navigate(routes.bookRequestList);
        }
      } else {
        showToast({
          title: "Error",
          text: result.error || `Could not ${status} the book request`,
          icon: "error",
        });
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || `Could not ${status} the book request`,
        icon: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={handleDelete}>
              <div className="modal-body text-center">
                <span style={deleteIconStyle}>
                  <i className="ti ti-trash" />
                </span>
                <h4>Delete Book Request</h4>
                <p className="mb-4">
                  Are you sure you want to delete this book request?
                  <br />
                  <small className="text-danger">
                    This action cannot be undone.
                  </small>
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Deleting...
                      </>
                    ) : (
                      "Yes, Delete"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      <div className="modal fade" id="approve-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                handleReview(e, "approved");
              }}
            >
              <div className="modal-body text-center">
                <span style={approveIconStyle}>
                  <i className="ti ti-check" />
                </span>
                <h4>Approve Book Request</h4>
                <p>
                  Are you sure you want to approve this book request?
                  <br />
                  This will mark the request as approved and notify the center.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-success"
                    data-bs-dismiss="modal"
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Approving...
                      </>
                    ) : (
                      "Yes, Approve"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <div className="modal fade" id="reject-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                handleReview(e, "rejected");
              }}
            >
              <div className="modal-body text-center">
                <span style={rejectIconStyle}>
                  <i className="ti ti-x" />
                </span>
                <h4>Reject Book Request</h4>
                <p className="mb-4">
                  Are you sure you want to reject this book request?
                </p>
                <div className="form-group mb-3">
                  <label htmlFor="rejectReason" className="form-label">
                    Rejection Reason:
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) =>
                      setRejectReason && setRejectReason(e.target.value)
                    }
                    className="form-control"
                    id="rejectReason"
                    name="rejectReason"
                    placeholder="Enter rejection reason"
                    rows={3}
                    required
                  />
                </div>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    data-bs-dismiss="modal"
                    disabled={isProcessing || !rejectReason.trim()}
                  >
                    {isProcessing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Rejecting...
                      </>
                    ) : (
                      "Yes, Reject"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Complete Modal */}
      <div className="modal fade" id="complete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form
              onSubmit={(e) => {
                handleReview(e, "completed");
              }}
            >
              <div className="modal-body text-center">
                <span style={completeIconStyle}>
                <i className="feather-check-square" />
                </span>
                <h4>Mark as Delivered</h4>
                <p>
                  Are you sure you want to mark this book request as delivered?
                  <br />
                  This will mark the request as completed.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-info"
                    data-bs-dismiss="modal"
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Processing...
                      </>
                    ) : (
                      "Yes, Mark as Delivered"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookRequestModals;
